// What an agent costs, and what a tier implies before one has ever run.
//
// Prices are DATA, deliberately. They change — one model's introductory rate
// has already lapsed — and a table baked into code silently misreports from the
// day it drifts. Every stored cost records which version priced it, so history
// stays explainable after a change instead of being quietly rewritten.

export const PRICE_VERSION = '2026-09';

/**
 * Per million tokens, by the tier a user actually picks. The tier is the unit
 * here because that is what the agent stores and what the user chose; which
 * model sits behind a tier is a product decision that can move without every
 * historical figure becoming wrong.
 */
export const TIER_PRICING = {
  fast:     { label: 'Efficient', inputPerM: 1, outputPerM: 5 },
  standard: { label: 'Balanced',  inputPerM: 3, outputPerM: 15 },
  smart:    { label: 'Expert',    inputPerM: 5, outputPerM: 25 },
};

export const DEFAULT_TIER = 'standard';

// Cached input is billed at a fraction of fresh input; writing to the cache
// costs a small premium over it. This is the single biggest lever on agent
// cost, because the charter and knowledge an agent re-reads never change
// between runs.
const CACHE_READ_MULTIPLIER  = 0.1;
const CACHE_WRITE_MULTIPLIER = 1.25;

export function tierPricing(tier) {
  return TIER_PRICING[tier] || TIER_PRICING[DEFAULT_TIER];
}

/**
 * Cost in USD of one run's actual usage. Pass what the API reported — never a
 * reconstruction from an estimate.
 */
export function costOfUsage(usage = {}, tier = DEFAULT_TIER) {
  const p = tierPricing(tier);
  const input      = Math.max(0, usage.inputTokens      || 0);
  const output     = Math.max(0, usage.outputTokens     || 0);
  const cacheRead  = Math.max(0, usage.cacheReadTokens  || 0);
  const cacheWrite = Math.max(0, usage.cacheWriteTokens || 0);
  return (
    (input      / 1e6) * p.inputPerM +
    (output     / 1e6) * p.outputPerM +
    (cacheRead  / 1e6) * p.inputPerM * CACHE_READ_MULTIPLIER +
    (cacheWrite / 1e6) * p.inputPerM * CACHE_WRITE_MULTIPLIER
  );
}

/** Short money for a UI. Sub-cent amounts are real but not worth four decimals. */
export function formatUsd(usd) {
  if (!isFinite(usd) || usd <= 0) return '$0.00';
  if (usd < 0.01) return '<$0.01';
  if (usd < 1)    return `$${usd.toFixed(2)}`;
  if (usd < 1000) return `$${usd.toFixed(2)}`;
  return `$${(usd / 1000).toFixed(1)}k`;
}

/**
 * The three figures that actually tell a user something, per the spec.
 *
 * Total alone finds the busiest agent, not the wasteful one. Cost per completed
 * outcome is what exposes an agent burning runs that end in failure — cheap per
 * run, expensive per result.
 */
export function agentSpend(runs = []) {
  const total     = runs.reduce((s, r) => s + (r.cost?.usd || 0), 0);
  const completed = runs.filter(r => r.outcome === 'completed' || r.status === 'completed').length;
  return {
    runs: runs.length,
    completed,
    totalUsd: total,
    perRunUsd: runs.length ? total / runs.length : 0,
    // Undefined rather than Infinity when nothing has succeeded: "no completed
    // runs yet" is a different statement from "infinitely expensive", and a UI
    // should say so rather than render ∞.
    perCompletedUsd: completed ? total / completed : undefined,
  };
}

/**
 * Rough cost of a typical task at a tier, for choosing one BEFORE any agent has
 * run. Deliberately coarse and labelled as an estimate wherever it is shown —
 * real spend varies more with the size of the job than with the tier.
 */
export function estimateTypicalTask(tier = DEFAULT_TIER, task = {}) {
  const inputTokens      = task.inputTokens      ?? 500_000;
  const outputTokens     = task.outputTokens     ?? 40_000;
  const cachedFraction   = task.cachedFraction   ?? 0.7;
  const fresh  = Math.round(inputTokens * (1 - cachedFraction));
  const cached = inputTokens - fresh;
  return costOfUsage(
    { inputTokens: fresh, outputTokens, cacheReadTokens: cached },
    tier,
  );
}
